import {
  useState,
  useEffect,
  useCallback,
  type ReactNode,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import {
  PlusCircleIcon,
  Loader2Icon,
  Copy,
  Check,
  RefreshCw,
  CreditCard,
  ChevronDownIcon,
  QrCodeIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DepositsApi } from "@/backend/api-client";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { OnramperWidget } from "@/features/onramper";
import { QRCode } from "react-qrcode-logo";

// Solana imports
import { SOLANA_NETWORK } from "@/solana/constants";

// Site's green color
const SITE_GREEN = "#2ebd85";
const SITE_GREEN_LIGHT = "#5fd4a4";

// =============================================================================
// ASSET & CHAIN CONFIGURATION (Future-proof for multi-asset/chain support)
// =============================================================================

type Asset = {
  id: string;
  name: string;
  symbol: string;
  enabled: boolean;
  comingSoon?: boolean;
};

type Chain = {
  id: string;
  name: string;
  enabled: boolean;
  comingSoon?: boolean;
  speed?: "fast" | "medium" | "slow";
};

const SUPPORTED_ASSETS: Asset[] = [
  { id: "usdc", name: "USD Coin", symbol: "USDC", enabled: true },
  { id: "usdt", name: "Tether", symbol: "USDT", enabled: true },
];

const SUPPORTED_CHAINS: Chain[] = [
  { id: "solana", name: "Solana", enabled: true, speed: "fast" },
  { id: "arbitrum", name: "Arbitrum", enabled: true, speed: "medium" },
  { id: "ethereum", name: "Ethereum", enabled: true, speed: "slow" },
];

// =============================================================================
// ICONS
// =============================================================================

const SolanaIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 128 128" fill="none">
    <defs>
      <linearGradient id="solana-grad-a" x1="94" y1="105" x2="45" y2="20" gradientUnits="userSpaceOnUse">
        <stop stopColor="#00FFA3" /><stop offset="1" stopColor="#DC1FFF" />
      </linearGradient>
      <linearGradient id="solana-grad-b" x1="76" y1="115" x2="28" y2="30" gradientUnits="userSpaceOnUse">
        <stop stopColor="#00FFA3" /><stop offset="1" stopColor="#DC1FFF" />
      </linearGradient>
      <linearGradient id="solana-grad-c" x1="85" y1="110" x2="37" y2="25" gradientUnits="userSpaceOnUse">
        <stop stopColor="#00FFA3" /><stop offset="1" stopColor="#DC1FFF" />
      </linearGradient>
    </defs>
    <path d="M36.2 85.5c.9-.9 2.1-1.4 3.4-1.4h58.8c2.1 0 3.2 2.6 1.7 4.1l-11.6 11.6c-.9.9-2.1 1.4-3.4 1.4H26.3c-2.1 0-3.2-2.6-1.7-4.1l11.6-11.6z" fill="url(#solana-grad-a)" />
    <path d="M36.2 28.8c.9-.9 2.2-1.4 3.4-1.4h58.8c2.1 0 3.2 2.6 1.7 4.1L88.5 43.1c-.9.9-2.1 1.4-3.4 1.4H26.3c-2.1 0-3.2-2.6-1.7-4.1l11.6-11.6z" fill="url(#solana-grad-b)" />
    <path d="M88.5 56.9c-.9-.9-2.1-1.4-3.4-1.4H26.3c-2.1 0-3.2 2.6-1.7 4.1l11.6 11.6c.9.9 2.1 1.4 3.4 1.4h58.8c2.1 0 3.2-2.6 1.7-4.1L88.5 56.9z" fill="url(#solana-grad-c)" />
  </svg>
);

const EthereumIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 128 128" fill="none">
    <path d="M64 8L25 65l39 23 39-23L64 8z" fill="#627EEA" />
    <path d="M64 8v42l39 15L64 8z" fill="#627EEA" fillOpacity="0.6" />
    <path d="M64 96l39-23-39 47V96z" fill="#627EEA" />
    <path d="M64 96v24l-39-47 39 23z" fill="#627EEA" fillOpacity="0.6" />
    <path d="M64 88l39-23-39-15v38z" fill="#627EEA" fillOpacity="0.2" />
    <path d="M25 65l39 23V50L25 65z" fill="#627EEA" fillOpacity="0.6" />
  </svg>
);

const ArbitrumIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 128 128" fill="none">
    <circle cx="64" cy="64" r="60" fill="#213147" />
    <path d="M80 92L64 48 48 92h-12L64 32l28 60h-12z" fill="#28A0F0" />
    <path d="M64 48l16 44h-12L64 82l-4 10h-12L64 48z" fill="white" />
  </svg>
);

const UsdcIcon = ({ className }: { className?: string }) => (
  <img src="/icons/tokens/usdc.svg" alt="USDC" className={className} />
);

const UsdtIcon = ({ className }: { className?: string }) => (
  <img src="/icons/tokens/usdt.svg" alt="USDT" className={className} />
);

// =============================================================================
// COMPONENT: Chain Selector Row
// =============================================================================

const ChainIcon = ({ chainId, className }: { chainId: string; className?: string }) => {
  switch (chainId) {
    case "solana":
      return <SolanaIcon className={className} />;
    case "ethereum":
      return <EthereumIcon className={className} />;
    case "arbitrum":
      return <ArbitrumIcon className={className} />;
    default:
      return null;
  }
};

const AssetIcon = ({ assetId, className }: { assetId: string; className?: string }) => {
  switch (assetId) {
    case "usdc":
      return <UsdcIcon className={className} />;
    case "usdt":
      return <UsdtIcon className={className} />;
    default:
      return null;
  }
};

// Inject keyframes for the animated glow border
const injectStyles = () => {
  const styleId = "deposit-btn-glow-animation";
  if (document.getElementById(styleId)) return;

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    @keyframes deposit-glow-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes deposit-glow-pulse {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 0.7; }
    }
  `;
  document.head.appendChild(style);
};

type DepositButtonProps = {
  trigger?: ReactNode;
  className?: string;
};

export type DepositButtonHandle = {
  openDialog: () => void;
  closeDialog: () => void;
};

export const DepositButton = forwardRef<DepositButtonHandle, DepositButtonProps>(
  ({ trigger, className }: DepositButtonProps = {}, ref) => {
    const { userId, isAuthenticated } = useUnifiedAuth();

    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isBuyCryptoExpanded, _setIsBuyCryptoExpanded] = useState(false);
    const [qrPopoverOpen, setQrPopoverOpen] = useState(false);

    // Asset & Chain selection (future-proof, currently only USDC on Solana)
    const [selectedAsset, setSelectedAsset] = useState("usdc");
    const [selectedChain, setSelectedChain] = useState("solana");

    // Inject animation styles on mount
    useEffect(() => {
      injectStyles();
    }, []);

    // Get effective user ID (works for both wallet and email users)
    const effectiveUserId = userId;
    const isEffectivelyConnected = isAuthenticated;

    // Fetch deposit address
    const {
      data: depositData,
      isLoading,
      error,
      refetch,
    } = useQuery({
      queryKey: ["deposit-address", effectiveUserId, selectedChain],
      queryFn: () => (effectiveUserId ? DepositsApi.getDepositAddress(selectedChain) : null),
      enabled: !!effectiveUserId && isEffectivelyConnected && open,
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
      retry: 2,
    });


    const copyToClipboard = useCallback(async () => {
      if (!depositData?.deposit_address) return;

      try {
        await navigator.clipboard.writeText(depositData.deposit_address);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    }, [depositData?.deposit_address]);

    useImperativeHandle(
      ref,
      () => ({
        openDialog: () => setOpen(true),
        closeDialog: () => setOpen(false),
      }),
      []
    );

    if (!isEffectivelyConnected) {
      return null;
    }

    // Get selected asset and chain info
    const currentAsset = SUPPORTED_ASSETS.find(a => a.id === selectedAsset) || SUPPORTED_ASSETS[0];
    const currentChain = SUPPORTED_CHAINS.find(c => c.id === selectedChain) || SUPPORTED_CHAINS[0];

    const defaultTrigger = (
      <button
        className={cn(
          "group relative inline-flex h-9 items-center justify-center gap-1.5 rounded-md px-4 text-sm font-medium transition-all duration-300 hover:scale-[1.02] cursor-pointer",
          className
        )}
        style={{ color: SITE_GREEN }}
        type="button"
      >
        {/* Outer glow effect */}
        <span
          className="absolute -inset-[3px] rounded-lg opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-500"
          style={{ background: `radial-gradient(circle, ${SITE_GREEN}4D 0%, transparent 70%)` }}
        />

        {/* Animated rotating gradient border - the traveling light */}
        <span className="absolute inset-0 rounded-md overflow-hidden p-[1px]">
          <span
            className="absolute inset-[-150%] group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: `conic-gradient(from 0deg, transparent 60%, ${SITE_GREEN}99 75%, ${SITE_GREEN_LIGHT}E6 85%, ${SITE_GREEN}99 95%, transparent 100%)`,
              animation: 'deposit-glow-spin 3s linear infinite',
              opacity: 0.6,
            }}
          />
        </span>

        {/* Subtle pulsing border for extra life */}
        <span
          className="absolute inset-0 rounded-md"
          style={{
            border: `1px solid ${SITE_GREEN}4D`,
            animation: 'deposit-glow-pulse 2s ease-in-out infinite'
          }}
        />

        {/* Button background - glass effect */}
        <span className="absolute inset-[1px] rounded-[5px] bg-background/95 backdrop-blur-sm" />

        {/* Content */}
        <span className="relative flex items-center gap-1.5">
          <PlusCircleIcon className="size-4" />
          <span className="hidden sm:inline">Deposit</span>
        </span>
      </button>
    );

    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <button onClick={() => setOpen(true)} className="contents">
          {trigger ?? defaultTrigger}
        </button>
        <DialogContent className="sm:max-w-[460px] px-6 bg-card" overlayClassName="bg-black/85">
          <DialogHeader>
            <DialogTitle>Deposit</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="space-y-3 text-center py-4">
                <p className="text-sm text-destructive">Failed to load deposit address</p>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  <RefreshCw className="size-4 mr-2" />
                  Retry
                </Button>
              </div>
            ) : depositData?.deposit_address ? (
              <>
                {/* ASSET/CHAIN + INFO grouped together */}
                <div className="space-y-2">
                  {/* ASSET & CHAIN SELECTORS */}
                  <div className="flex gap-3">
                    {/* Asset Selector */}
                    <Select value={selectedAsset} onValueChange={setSelectedAsset}>
                      <SelectTrigger className="flex-1 h-10 bg-muted/30 border border-border/50">
                        <div className="flex items-center justify-between w-full pr-2">
                          <span className="text-xs text-muted-foreground">Asset</span>
                          <div className="flex items-center gap-1.5">
                            <AssetIcon assetId={selectedAsset} className="size-5 rounded-full" />
                            <span className="font-medium text-sm">{currentAsset.symbol}</span>
                          </div>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {SUPPORTED_ASSETS.map(asset => (
                          <SelectItem
                            key={asset.id}
                            value={asset.id}
                            disabled={!asset.enabled}
                          >
                            <div className="flex items-center justify-between w-full gap-3">
                              <div className="flex items-center gap-2">
                                <AssetIcon assetId={asset.id} className="size-5 rounded-full" />
                                <span>{asset.symbol}</span>
                              </div>
                              {asset.comingSoon && (
                                <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                  Soon
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Chain Selector */}
                    <Select value={selectedChain} onValueChange={setSelectedChain}>
                      <SelectTrigger className="flex-1 h-10 bg-muted/30 border border-border/50">
                        <div className="flex items-center justify-between w-full pr-2">
                          <span className="text-xs text-muted-foreground">Chain</span>
                          <div className="flex items-center gap-1.5">
                            <ChainIcon chainId={selectedChain} className="size-5" />
                            <span className="font-medium text-sm">{currentChain.name}</span>
                          </div>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {SUPPORTED_CHAINS.map(chain => (
                          <SelectItem
                            key={chain.id}
                            value={chain.id}
                            disabled={!chain.enabled}
                          >
                            <div className="flex items-center justify-between w-full gap-3">
                              <div className="flex items-center gap-2">
                                <ChainIcon chainId={chain.id} className="size-5" />
                                <span className="w-20">{chain.name}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {/* Speed indicator - always show 3 bolts, fill based on speed */}
                                {chain.speed && (
                                  <span className="flex items-center text-sm">
                                    <span className="text-[#2ebd85]">⚡</span>
                                    <span className={chain.speed === "fast" || chain.speed === "medium" ? "text-[#2ebd85]" : "text-muted-foreground/40"}>⚡</span>
                                    <span className={chain.speed === "fast" ? "text-[#2ebd85]" : "text-muted-foreground/40"}>⚡</span>
                                  </span>
                                )}
                                {chain.comingSoon && (
                                  <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded ml-1">
                                    Soon
                                  </span>
                                )}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* DEPOSIT INFO - Minimum on left (asset), Arrival on right (chain) */}
                  <div className="flex justify-between text-[11px] text-muted-foreground px-1">
                    <div className="flex items-center gap-1">
                      <span>Minimum Deposit</span>
                      <span className="text-foreground font-medium">10 {currentAsset.symbol}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>Arrival Time</span>
                      <span className="text-foreground font-medium">≈ {currentChain.speed === "fast" ? "1 min" : currentChain.speed === "medium" ? "3-5 min" : "10-15 min"}</span>
                    </div>
                  </div>

                </div>

                {/* Subtle divider - centered between sections */}
                <div className="border-t border-border/70 w-1/2 mx-auto my-3" />

                {/* DEPOSIT ADDRESS */}
                <div className="space-y-1.5 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-muted-foreground font-medium">Your Deposit Address</label>
                    <span className="text-[10px] text-muted-foreground/70">Deposits auto-detected ✓</span>
                  </div>
                  <div className="bg-muted/50 border rounded-lg py-2 px-3 font-mono text-[13px] break-all select-all text-center">
                    {depositData.deposit_address}
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyToClipboard}
                      className="flex-1 h-9"
                    >
                      {copied ? (
                        <>
                          <Check className="size-4 mr-2 text-bid-foreground" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="size-4 mr-2" />
                          Copy Address
                        </>
                      )}
                    </Button>

                    {/* QR Code Popover */}
                    <Popover open={qrPopoverOpen} onOpenChange={setQrPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 px-3"
                        >
                          <QrCodeIcon className="size-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-4" align="center">
                        <div className="flex flex-col items-center gap-3">
                          <p className="text-xs text-muted-foreground font-medium">Scan to get address</p>
                          <div className="bg-white rounded-xl p-2.5 shadow-sm">
                            <QRCode
                              value={depositData.deposit_address}
                              size={180}
                              bgColor="#ffffff"
                              fgColor="#1a1a1a"
                              qrStyle="dots"
                              eyeRadius={[
                                { outer: 10, inner: 3 },
                                { outer: 10, inner: 3 },
                                { outer: 10, inner: 3 },
                              ]}
                              eyeColor={SITE_GREEN}
                              logoImage="/favicon.png"
                              logoWidth={36}
                              logoHeight={36}
                              logoPadding={4}
                              logoPaddingStyle="circle"
                              removeQrCodeBehindLogo={true}
                              ecLevel="H"
                              quietZone={6}
                            />
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* WARNING NOTICE - Plain text */}
                  <p className="text-[11px] text-amber-500/80 text-left px-1">
                    Only {currentAsset.symbol} ({currentChain.name}) is supported for deposits to this address.
                  </p>
                </div>

                {/* SECONDARY: Collapsible Buy Crypto Section - Currently Disabled */}
                <div
                  className="border rounded-lg overflow-hidden transition-all duration-300 mt-1 opacity-60"
                  style={{
                    borderColor: 'rgba(46, 189, 133, 0.2)'
                  }}
                >
                  <button
                    disabled
                    className="w-full py-3 px-4 flex items-center justify-between transition-all duration-300 group cursor-not-allowed"
                    style={{
                      backgroundColor: 'transparent'
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="p-1.5 rounded-lg transition-all duration-300"
                        style={{
                          backgroundColor: `${SITE_GREEN}15`,
                          border: `1px solid ${SITE_GREEN}30`
                        }}
                      >
                        <CreditCard className="size-4 transition-all duration-300" style={{ color: SITE_GREEN }} />
                      </div>
                      <p className="text-sm font-bold transition-colors">
                        Need Crypto?
                      </p>
                    </div>

                    {/* Payment icons + chevron on the right */}
                    <div className="flex items-center gap-3">
                      {/* Payment method icons */}
                      <div className="hidden sm:flex items-center gap-2 opacity-50 transition-opacity">
                        {/* Visa */}
                        <svg width="28" height="20" viewBox="0 0 48 32" fill="none">
                          <rect width="48" height="32" rx="3" fill="currentColor" opacity="0.1" />
                          <path d="M20.4 11.2L17.6 20.8H15.2L13.6 13.6C13.5 13.2 13.4 13 13.1 12.8C12.6 12.5 11.8 12.2 11.2 12V11.2H15.6C16.2 11.2 16.7 11.6 16.8 12.3L17.6 17.2L19.6 11.2H22L20.4 11.2ZM31.6 17.9C31.6 20.4 28.8 20.6 28.8 19.4C28.8 18.9 29.3 18.6 30.2 18.5C30.8 18.4 31.1 18.4 31.6 18.2V17.9ZM34 20.8H31.8C31.7 20.4 31.6 20.1 31.6 19.6C31 20.3 30 20.6 29.1 20.6C27.5 20.6 26.4 19.6 26.4 18.2C26.4 16.3 28.2 15.5 30.8 15.5H31.6V15.2C31.6 14.5 31.2 14.1 30.3 14.1C29.5 14.1 28.7 14.4 28.2 14.7L27.7 12.8C28.4 12.4 29.4 12 30.6 12C33 12 34 13.2 34 15.2V20.8ZM23.2 20.8V11.2H25.6V20.8H23.2ZM11.2 11.2L8.8 20.8H6.4L4 11.2H6.4L7.6 17.2L8.8 11.2H11.2Z" fill="currentColor" />
                        </svg>

                        {/* Mastercard */}
                        <svg width="28" height="20" viewBox="0 0 48 32" fill="none">
                          <rect width="48" height="32" rx="3" fill="currentColor" opacity="0.1" />
                          <circle cx="18" cy="16" r="6" fill="currentColor" opacity="0.35" />
                          <circle cx="30" cy="16" r="6" fill="currentColor" opacity="0.35" />
                          <path d="M24 11.5C25.2 12.6 26 14.2 26 16C26 17.8 25.2 19.4 24 20.5C22.8 19.4 22 17.8 22 16C22 14.2 22.8 12.6 24 11.5Z" fill="currentColor" opacity="0.5" />
                        </svg>

                        {/* Apple Pay */}
                        <svg width="28" height="20" viewBox="0 0 48 32" fill="none">
                          <rect width="48" height="32" rx="3" fill="currentColor" opacity="0.1" />
                          <path d="M16.5 11C17.2 11 18.2 10.4 18.8 9.6C19.3 8.9 19.7 7.9 19.7 6.9C19.7 6.7 19.7 6.6 19.6 6.5C18.7 6.5 17.6 7.2 17 7.9C16.5 8.5 16 9.4 16 10.4C16 10.6 16 10.7 16.1 10.8C16.2 10.9 16.3 11 16.5 11ZM13.5 25.5C14.8 25.5 15.3 24.6 17 24.6C18.7 24.6 19 25.5 20.5 25.5C21.9 25.5 22.9 24.1 23.8 22.7C24.8 21.1 25.2 19.6 25.2 19.5C25.2 19.4 23 18.5 23 16C23 13.9 24.7 13 24.8 12.9C23.7 11.3 22 11.2 21.5 11.2C19.9 11.2 18.7 12.2 17.9 12.2C17 12.2 16 11.3 14.6 11.3C12.2 11.3 10 13.3 10 17.2C10 19.4 10.8 21.7 11.8 23.3C12.6 24.6 13.3 25.5 13.5 25.5Z" fill="currentColor" />
                        </svg>

                        {/* Bank */}
                        <svg width="28" height="20" viewBox="0 0 48 32" fill="none">
                          <rect width="48" height="32" rx="3" fill="currentColor" opacity="0.1" />
                          <path d="M24 8L12 14V15H36V14L24 8ZM14 17V23H16V17H14ZM18 17V23H20V17H18ZM22 17V23H24V17H22ZM26 17V23H28V17H26ZM30 17V23H32V17H30ZM12 25V27H36V25H12Z" fill="currentColor" />
                        </svg>
                      </div>

                      {/* Soon Badge */}
                      <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded font-medium">
                        Soon
                      </span>

                      {/* Chevron arrow */}
                      <div
                        className="transition-all duration-300"
                        style={{
                          transform: isBuyCryptoExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}
                      >
                        <ChevronDownIcon className="size-5" />
                      </div>
                    </div>
                  </button>

                  {/* Expanded content - preserved but won't show since button is disabled */}
                  {isBuyCryptoExpanded && (
                    <div className="px-5 pb-6 space-y-5 border-t">
                      <div className="pt-6 space-y-3">
                        {/* Step 1 */}
                        <div className="flex items-start gap-3.5">
                          <div
                            className="flex items-center justify-center size-6 rounded-full text-xs font-bold text-white flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: SITE_GREEN }}
                          >
                            1
                          </div>
                          <div>
                            <p className="text-sm font-medium">Buy USDC with Card</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Use Visa, Mastercard, or Apple Pay via our trusted partners
                            </p>
                          </div>
                        </div>

                        {/* Step 2 */}
                        <div className="flex items-start gap-3.5">
                          <div
                            className="flex items-center justify-center size-6 rounded-full text-xs font-bold text-white flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: SITE_GREEN }}
                          >
                            2
                          </div>
                          <div>
                            <p className="text-sm font-medium">Receive in Your Wallet</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              USDC arrives automatically in your private wallet
                            </p>
                          </div>
                        </div>

                        {/* Step 3 */}
                        <div className="flex items-start gap-3.5">
                          <div
                            className="flex items-center justify-center size-6 rounded-full text-xs font-bold text-white flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: SITE_GREEN }}
                          >
                            3
                          </div>
                          <div>
                            <p className="text-sm font-medium">Deposit & Start Trading</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              One click to move funds to your trading account
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Buy Crypto Button */}
                      <OnramperWidget
                        depositAddress={depositData.deposit_address}
                        userId={effectiveUserId!}
                        onSuccess={() => {
                          refetch();
                        }}
                        trigger={
                          <Button className="w-full h-12 text-base font-medium" style={{ backgroundColor: SITE_GREEN }}>
                            <CreditCard className="size-5 mr-2" />
                            Buy USDC with Card
                          </Button>
                        }
                      />
                    </div>
                  )}
                </div>

                {/* Network notice */}
                {SOLANA_NETWORK === "devnet" && (
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                    <p className="text-xs text-yellow-600 dark:text-yellow-400">
                      <strong>Devnet:</strong> This is a test environment. Only send devnet USDC.
                    </p>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    );
  });

DepositButton.displayName = "DepositButton";
